"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArbitrumRelayerFee = void 0;
const ethers_1 = require("ethers");
const models_1 = require("../models");
const constants_1 = require("../constants");
const config_1 = require("../config");
class ArbitrumRelayerFee {
    constructor(network, token) {
        this.network = network;
        this.token = token;
    }
    getRelayCost() {
        return __awaiter(this, void 0, void 0, function* () {
            const arbitrumRpcUrl = config_1.config.chains[this.network][models_1.Chain.Arbitrum.slug].rpcUrl;
            const provider = new ethers_1.providers.StaticJsonRpcProvider({ url: arbitrumRpcUrl });
            // Submission Cost
            const l1RpcUrl = config_1.config.chains[this.network][models_1.Chain.Ethereum.slug].rpcUrl;
            const l1Provider = new ethers_1.providers.StaticJsonRpcProvider({ url: l1RpcUrl });
            const distributeCalldataSize = 196;
            const { baseFeePerGas } = yield l1Provider.getBlock('latest');
            const submissionCost = this._calculateRetryableSubmissionFee(distributeCalldataSize, baseFeePerGas);
            // Redemption Cost
            const encodedRedemptionData = yield this._getEncodedGasInfo();
            const arbGasInfo = '0x000000000000000000000000000000000000006C';
            const redemptionTx = {
                to: arbGasInfo,
                data: encodedRedemptionData
            };
            const gasInfo = yield provider.call(redemptionTx);
            const types = ['uint256', 'uint256', 'uint256', 'uint256', 'uint256', 'uint256'];
            const decoded = ethers_1.utils.defaultAbiCoder.decode(types, gasInfo);
            const redemptionGasPrice = decoded[1];
            const redemptionGasLimit = ethers_1.BigNumber.from(1000);
            const redemptionCost = redemptionGasLimit.mul(redemptionGasPrice);
            // Distribution Cost
            const encodedDistributeData = yield this._getEncodedDistributeData();
            const encodedEstimateRetryableTicketData = yield this._getEncodedEstimateRetryableTicketData(encodedDistributeData);
            const nodeInterfaceAddress = '0x00000000000000000000000000000000000000C8';
            const distributionTx = {
                to: nodeInterfaceAddress,
                from: this._getBonderAddress(),
                data: encodedEstimateRetryableTicketData
            };
            const distributionGasLimit = yield provider.estimateGas(distributionTx);
            const distributionGasPrice = yield provider.getGasPrice();
            const distributionCost = distributionGasLimit.mul(distributionGasPrice);
            const totalCost = submissionCost.add(redemptionCost).add(distributionCost);
            return totalCost;
        });
    }
    _calculateRetryableSubmissionFee(dataLength, baseFee) {
        const dataCost = 1400 + 6 * dataLength;
        return ethers_1.BigNumber.from(dataCost).mul(baseFee);
    }
    _getEncodedGasInfo() {
        return __awaiter(this, void 0, void 0, function* () {
            const abi = ['function getPricesInWei()'];
            const ethersInterface = new ethers_1.utils.Interface(abi);
            const encodedData = ethersInterface.encodeFunctionData('getPricesInWei', []);
            return encodedData;
        });
    }
    _getEncodedDistributeData() {
        return __awaiter(this, void 0, void 0, function* () {
            // Do not use the zero address since some ERC20 tokens throw when sending to the zero address
            const recipient = '0x0000000000000000000000000000000000000001';
            const amount = ethers_1.BigNumber.from(10);
            const amountOutMin = ethers_1.BigNumber.from(0);
            const deadline = ethers_1.BigNumber.from(constants_1.MaxDeadline);
            const relayer = this._getBonderAddress();
            const relayerFee = ethers_1.BigNumber.from(1);
            const abi = ['function distribute(address recipient, uint256 amount, uint256 amountOutMin, uint256 deadline, address relayer, uint256 relayerFee)'];
            const ethersInterface = new ethers_1.utils.Interface(abi);
            const encodedData = ethersInterface.encodeFunctionData('distribute', [
                recipient,
                amount,
                amountOutMin,
                deadline,
                relayer,
                relayerFee
            ]);
            return encodedData;
        });
    }
    _getEncodedEstimateRetryableTicketData(encodedDistributeData) {
        return __awaiter(this, void 0, void 0, function* () {
            // The alias address on Arbitrum needs to have enough funds to cover the tx in order for this to work
            const messengerWrapperAddress = this._getMessengerWrapperAddress();
            const sender = messengerWrapperAddress;
            const deposit = ethers_1.BigNumber.from(0);
            const to = this._getL2BridgeAddress();
            const l2CallValue = ethers_1.BigNumber.from(0);
            const excessFeeRefundAddress = ethers_1.constants.AddressZero;
            const callValueRefundAddress = ethers_1.constants.AddressZero;
            const data = encodedDistributeData;
            const abi = ['function estimateRetryableTicket(address sender, uint256 deposit, address to, uint256 l2CallValue, address excessFeeRefundAddress, address callValueRefundAddress, bytes data)'];
            const ethersInterface = new ethers_1.utils.Interface(abi);
            const encodedData = ethersInterface.encodeFunctionData('estimateRetryableTicket', [
                sender,
                deposit,
                to,
                l2CallValue,
                excessFeeRefundAddress,
                callValueRefundAddress,
                data
            ]);
            return encodedData;
        });
    }
    _getMessengerWrapperAddress() {
        var _a, _b, _c, _d;
        const messengerWrapperAddress = (_d = (_c = (_b = (_a = config_1.config === null || config_1.config === void 0 ? void 0 : config_1.config.addresses) === null || _a === void 0 ? void 0 : _a[this.network]) === null || _b === void 0 ? void 0 : _b[this.token]) === null || _c === void 0 ? void 0 : _c[models_1.Chain.Arbitrum.slug]) === null || _d === void 0 ? void 0 : _d.l1MessengerWrapper;
        if (!messengerWrapperAddress) {
            throw new Error('messengerWrapperAddress not found');
        }
        return messengerWrapperAddress;
    }
    _getL2BridgeAddress() {
        var _a, _b, _c, _d;
        const l2BridgeAddress = (_d = (_c = (_b = (_a = config_1.config === null || config_1.config === void 0 ? void 0 : config_1.config.addresses) === null || _a === void 0 ? void 0 : _a[this.network]) === null || _b === void 0 ? void 0 : _b[this.token]) === null || _c === void 0 ? void 0 : _c[models_1.Chain.Arbitrum.slug]) === null || _d === void 0 ? void 0 : _d.l2Bridge;
        if (!l2BridgeAddress) {
            throw new Error('l2BridgeAddress not found');
        }
        return l2BridgeAddress;
    }
    _getBonderAddress() {
        var _a, _b, _c, _d;
        const bonderAddress = (_d = (_c = (_b = (_a = config_1.config === null || config_1.config === void 0 ? void 0 : config_1.config.bonders) === null || _a === void 0 ? void 0 : _a[this.network]) === null || _b === void 0 ? void 0 : _b[this.token]) === null || _c === void 0 ? void 0 : _c[models_1.Chain.Arbitrum.slug]) === null || _d === void 0 ? void 0 : _d[models_1.Chain.Ethereum.slug];
        if (!bonderAddress) {
            throw new Error('bonderAddress not found');
        }
        return bonderAddress;
    }
}
exports.ArbitrumRelayerFee = ArbitrumRelayerFee;
